'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Home,
  DollarSign,
  Percent,
  Calendar,
  BarChart3,
  Currency,
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useLanguage } from '@/app/context/language-context';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function RealEstateCalculator() {
  const [calculatorMode, setCalculatorMode] = useState('hold'); // "hold" or "flip"
  const [currency, setCurrency] = useState('AED');
  const { t } = useLanguage();

  // Exchange rate (AED to USD) - you can make this dynamic by fetching from an API
  const AED_TO_USD_RATE = 0.272; // 1 AED = 0.272 USD (approximate)

  // Hold Calculator State
  const [holdInputs, setHoldInputs] = useState({
    investmentAmount: '50000',
    expectedAppreciation: '6.0',
    expectedRentalYield: '2.0',
    holdingPeriod: '5',
  });

  // Flip Calculator State
  const [flipInputs, setFlipInputs] = useState({
    investmentAmount: '50000',
    expectedIRR: '12.0',
    projectDuration: '2',
    exitMultiple: '1.6',
  });

  const [holdResults, setHoldResults] = useState({
    totalValue: 0,
    capitalAppreciation: 0,
    totalRentalIncome: 0,
    totalReturn: 0,
    annualizedReturn: 0,
    yearlyData: [],
  });

  const [flipResults, setFlipResults] = useState({
    totalROI: 0,
    totalReturn: 0,
    annualizedReturn: 0,
    exitValue: 0,
  });

  // Input validation and formatting functions
  const formatNumberInput = (value, allowDecimals = true) => {
    // Remove any non-numeric characters except decimal point
    let cleaned = value.replace(/[^\d.]/g, '');

    if (!allowDecimals) {
      cleaned = cleaned.replace(/\./g, '');
    } else {
      // Allow only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    return cleaned;
  };

  const parseNumberInput = (value) => {
    const parsed = Number.parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Currency conversion for display only (total values)
  const convertToDisplayCurrency = (aedAmount) => {
    if (currency === 'USD') {
      return aedAmount * AED_TO_USD_RATE;
    }
    return aedAmount;
  };

  // Calculate Hold Strategy Results
  useEffect(() => {
    const calculateHoldResults = () => {
      const investmentAmount = parseNumberInput(holdInputs.investmentAmount);
      const expectedAppreciation = parseNumberInput(
        holdInputs.expectedAppreciation
      );
      const expectedRentalYield = parseNumberInput(
        holdInputs.expectedRentalYield
      );
      const holdingPeriod = parseNumberInput(holdInputs.holdingPeriod);

      const appreciationRate = expectedAppreciation / 100;
      const rentalRate = expectedRentalYield / 100;

      const yearlyData = [];
      let cumulativeRental = 0;

      for (let year = 0; year <= holdingPeriod; year++) {
        const propertyValue =
          investmentAmount * Math.pow(1 + appreciationRate, year);
        const annualRental = investmentAmount * rentalRate;
        // const annualRental = propertyValue * rentalRate;
        cumulativeRental += year > 0 ? annualRental : 0;

        yearlyData.push({
          year,
          propertyValue,
          cumulativeRental,
          totalValue: propertyValue + cumulativeRental,
        });
      }

      const finalValue = yearlyData[holdingPeriod]?.propertyValue || 0;
      const totalRentalIncome = cumulativeRental;
      const capitalAppreciation = finalValue - investmentAmount;
      const totalReturn = capitalAppreciation + totalRentalIncome;

      // console.log(totalReturn + finalValue, 'totalReturn');

      const annualizedReturn =
        holdingPeriod > 0
          ? (Math.pow(finalValue / investmentAmount, 1 / holdingPeriod) - 1) *
            100
          : 0;

      setHoldResults({
        totalValue: finalValue + totalRentalIncome,
        capitalAppreciation,
        totalRentalIncome,
        totalReturn,
        annualizedReturn,
        yearlyData,
      });
    };

    calculateHoldResults();
  }, [holdInputs]);

  // Calculate Flip Strategy Results
  // useEffect(() => {
  //   const calculateFlipResults = () => {
  //     const investmentAmount = parseNumberInput(flipInputs.investmentAmount);
  //     const expectedIRR = parseNumberInput(flipInputs.expectedIRR) / 100;
  //     const projectDuration = parseNumberInput(flipInputs.projectDuration);
  //     const exitMultiple = parseNumberInput(flipInputs.exitMultiple);

  //     const exitValue = investmentAmount * exitMultiple;
  //     const totalReturn = exitValue - investmentAmount;
  //     const annualizedReturn =
  //       projectDuration > 0
  //         ? (Math.pow(exitValue / investmentAmount, 1 / projectDuration) - 1) *
  //           100
  //         : 0;

  //     setFlipResults({
  //       totalROI: totalReturn,
  //       totalReturn,
  //       annualizedReturn,
  //       exitValue,
  //     });
  //   };

  //   calculateFlipResults();
  // }, [flipInputs]);

  // ====================== Flip Calculator ======================
  useEffect(() => {
    const calculateFlipResults = () => {
      const investmentAmount = parseNumberInput(flipInputs.investmentAmount);
      const expectedIRR = parseNumberInput(flipInputs.expectedIRR); // keep as percentage

      const holdingPeriod = 5;
      const totalROI = investmentAmount * (expectedIRR / 100) * holdingPeriod;
      const exitValue = investmentAmount + totalROI;

      setFlipResults({
        totalROI,
        totalReturn: totalROI,
        annualizedReturn: expectedIRR,
        exitValue,
      });
    };

    calculateFlipResults();
  }, [flipInputs]);

  const formatCurrency = (amount) => {
    const displayAmount = convertToDisplayCurrency(amount);
    return new Intl.NumberFormat(currency === 'AED' ? 'en-AE' : 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(displayAmount);
  };

  // Chart configuration for Hold strategy
  const chartData = {
    labels: holdResults.yearlyData.map((data) => `Year ${data.year}`),
    datasets: [
      {
        label: 'Property Value',
        data: holdResults.yearlyData.map((data) =>
          convertToDisplayCurrency(data.propertyValue)
        ),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Total Value (Property + Rental)',
        data: holdResults.yearlyData.map((data) =>
          convertToDisplayCurrency(data.totalValue)
        ),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#ffffff',
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${new Intl.NumberFormat(
              currency === 'AED' ? 'en-AE' : 'en-US',
              {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }
            ).format(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
        },
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
          callback: (value) => {
            return new Intl.NumberFormat(
              currency === 'AED' ? 'en-AE' : 'en-US',
              {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }
            ).format(value);
          },
        },
      },
    },
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 max-w-6xl mx-auto'>
      {/* Header */}
      <div className='text-center mb-8'>
        <h2 className='text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4'>
          {t('dubai_real_estate_investments')}
        </h2>
        <p className='text-gray-600 dark:text-gray-300 text-lg'>
          {t('dubai_real_estate_investments_description')}
        </p>
      </div>

      {/* Strategy Toggle */}
      <div className='flex justify-center mb-8'>
        <div className='bg-gray-100 dark:bg-gray-700 rounded-full p-1 flex'>
          <button
            onClick={() => setCalculatorMode('hold')}
            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
              calculatorMode === 'hold'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:text-indigo-600'
            }`}
          >
            {t('dubai_real_estate_investments_hold')}
          </button>
          <button
            onClick={() => setCalculatorMode('flip')}
            className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
              calculatorMode === 'flip'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:text-purple-600'
            }`}
          >
            {t('dubai_real_estate_investments_flip')}
          </button>
        </div>
      </div>

      <AnimatePresence mode='wait'>
        {calculatorMode === 'hold' ? (
          <motion.div
            key='hold'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className='grid grid-cols-1 lg:grid-cols-2 gap-8'
          >
            {/* Chart Section */}
            <div className='bg-gray-900 rounded-2xl p-6'>
              <div className='h-80 mb-4'>
                <Line data={chartData} options={chartOptions} />
              </div>
              <div className='grid grid-cols-2 gap-4 text-white'>
                <div className='text-center'>
                  <div className='text-sm text-gray-400'>
                    {t('expected_capital_appreciation')}
                  </div>
                  <div className='text-lg font-bold text-indigo-400'>
                    {formatCurrency(holdResults.capitalAppreciation)}
                  </div>
                </div>
                <div className='text-center'>
                  <div className='text-sm text-gray-400'>
                    {t('expected_total_rental_income')}
                  </div>
                  <div className='text-lg font-bold text-purple-400'>
                    {formatCurrency(holdResults.totalRentalIncome)}
                  </div>
                </div>
              </div>
            </div>

            {/* Input Section */}
            <div className='space-y-6'>
              {/* Currency Toggle */}
              <div className='flex justify-end'>
                <div className='bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex'>
                  <button
                    onClick={() => setCurrency('AED')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all duration-200 ${
                      currency === 'AED'
                        ? 'bg-indigo-600 text-white shadow-lg'
                        : 'text-gray-600 dark:text-gray-300 hover:text-indigo-600'
                    }`}
                  >
                    {t('expected_aed')}
                  </button>
                  <button
                    onClick={() => setCurrency('USD')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all duration-200 ${
                      currency === 'USD'
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'text-gray-600 dark:text-gray-300 hover:text-purple-600'
                    }`}
                  >
                    {t('expected_usd')}
                  </button>
                </div>
              </div>

              {/* Investment Amount */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_investment_amount')}
                </label>
                <div className='relative'>
                  {currency === 'USD' ? (
                    <DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                  ) : (
                    <span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5'>
                      د.إ
                    </span>
                  )}

                  <input
                    type='text'
                    value={holdInputs.investmentAmount}
                    onChange={(e) => {
                      const formatted = formatNumberInput(
                        e.target.value,
                        false
                      );
                      setHoldInputs({
                        ...holdInputs,
                        investmentAmount: formatted,
                      });
                    }}
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                    placeholder='50000'
                  />
                </div>
              </div>

              {/* Expected Annual Appreciation */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_annual_appreciation')}
                </label>
                <div className='flex items-center space-x-4'>
                  <div className='relative flex-1'>
                    <Percent className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                    <input
                      type='text'
                      value={holdInputs.expectedAppreciation}
                      onChange={(e) => {
                        const formatted = formatNumberInput(
                          e.target.value,
                          true
                        );
                        setHoldInputs({
                          ...holdInputs,
                          expectedAppreciation: formatted,
                        });
                      }}
                      className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                      placeholder='6.0'
                    />
                  </div>
                  <div className='text-2xl font-bold text-indigo-600'>
                    {holdInputs.expectedAppreciation}%
                  </div>
                </div>
              </div>

              {/* Expected Annual Rental Yield */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_annual_rental_yield')}
                </label>
                <div className='flex items-center space-x-4'>
                  <div className='relative flex-1'>
                    <Home className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                    <input
                      type='text'
                      value={holdInputs.expectedRentalYield}
                      onChange={(e) => {
                        const formatted = formatNumberInput(
                          e.target.value,
                          true
                        );
                        setHoldInputs({
                          ...holdInputs,
                          expectedRentalYield: formatted,
                        });
                      }}
                      className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                      placeholder='2.0'
                    />
                  </div>
                  <div className='text-2xl font-bold text-purple-600'>
                    {holdInputs.expectedRentalYield}%
                  </div>
                </div>
              </div>

              {/* Holding Period */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_holding_period')}
                </label>
                <div className='relative'>
                  <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='text'
                    value={holdInputs.holdingPeriod}
                    onChange={(e) => {
                      const formatted = formatNumberInput(
                        e.target.value,
                        false
                      );
                      // Limit to reasonable range
                      const num = parseNumberInput(formatted);
                      if (num <= 30) {
                        setHoldInputs({
                          ...holdInputs,
                          holdingPeriod: formatted,
                        });
                      }
                    }}
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                    placeholder='5'
                  />
                </div>
              </div>

              {/* Results */}
              <div className='bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-indigo-200 dark:border-indigo-800'>
                <div className='text-center mb-4'>
                  <div className='text-sm text-gray-600 dark:text-gray-400'>
                    {t('expected_value_after')} {holdInputs.holdingPeriod}{' '}
                    {t('expected_years')}
                  </div>
                  <div className='text-3xl font-bold text-indigo-600'>
                    {formatCurrency(holdResults.totalValue)}
                  </div>
                  {currency === 'USD' && (
                    <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                      ≈{' '}
                      {new Intl.NumberFormat('en-AE', {
                        style: 'currency',
                        currency: 'AED',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(holdResults.totalValue)}{' '}
                      {t('expected_aed')}
                    </div>
                  )}
                </div>
                <div className='grid grid-cols-3 gap-4 text-center'>
                  <div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {t('expected_value_exit_fee')}
                    </div>
                    <div className='font-semibold text-gray-700 dark:text-gray-300'>
                      {formatCurrency(holdResults.totalValue * 0.025)}
                    </div>
                  </div>
                  <div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {t('expected_value_annual_cash_flow')}
                    </div>
                    <div className='font-semibold text-gray-700 dark:text-gray-300'>
                      {formatCurrency(
                        holdResults.totalRentalIncome /
                          parseNumberInput(holdInputs.holdingPeriod)
                      )}
                    </div>
                  </div>
                  <div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {t('expected_value_entry_fee')}
                    </div>
                    <div className='font-semibold text-gray-700 dark:text-gray-300'>
                      {formatCurrency(
                        parseNumberInput(holdInputs.investmentAmount) * 0.015
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reset Button */}
              <button
                onClick={() =>
                  setHoldInputs({
                    investmentAmount: '50000',
                    expectedAppreciation: '6.0',
                    expectedRentalYield: '2.0',
                    holdingPeriod: '5',
                  })
                }
                className='w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200'
              >
                {t('expected_reset')}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key='flip'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className='grid grid-cols-1 lg:grid-cols-2 gap-8'
          >
            {/* Visualization Section */}
            <div className='bg-gray-900 rounded-2xl p-6 flex items-center justify-center'>
              <div className='text-center text-white'>
                <div className='w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4'>
                  <Home className='w-8 h-8' />
                </div>
                <h3 className='text-2xl font-bold mb-2'>Calculate Your</h3>
                <h3 className='text-2xl font-bold mb-4'>Earnings</h3>
                <div className='space-y-2 text-sm text-gray-400'>
                  {/* <div>
                    {t('expected_reset')} {flipInputs.projectDuration}{' '}
                    {t('expected_years')}
                  </div> */}
                  <div>
                    {t('expected_expected_irr')}: {flipInputs.expectedIRR}%
                  </div>
                  <div>
                    {t('expected_exit_multiple')}: {flipInputs.exitMultiple}x
                  </div>
                </div>
              </div>
            </div>

            {/* Input Section */}
            <div className='space-y-6'>
              {/* Currency Toggle */}
              <div className='flex justify-end'>
                <div className='bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex'>
                  <button
                    onClick={() => setCurrency('AED')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all duration-200 ${
                      currency === 'AED'
                        ? 'bg-indigo-600 text-white shadow-lg'
                        : 'text-gray-600 dark:text-gray-300 hover:text-indigo-600'
                    }`}
                  >
                    {t('expected_aed')}
                  </button>
                  <button
                    onClick={() => setCurrency('USD')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all duration-200 ${
                      currency === 'USD'
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'text-gray-600 dark:text-gray-300 hover:text-purple-600'
                    }`}
                  >
                    {t('expected_usd')}
                  </button>
                </div>
              </div>

              {/* Investment Amount */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_investment_amount')}
                </label>
                <div className='relative'>
                  <DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='text'
                    value={flipInputs.investmentAmount}
                    onChange={(e) => {
                      const formatted = formatNumberInput(
                        e.target.value,
                        false
                      );
                      setFlipInputs({
                        ...flipInputs,
                        investmentAmount: formatted,
                      });
                    }}
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                    placeholder='50000'
                  />
                </div>
              </div>

              {/* Expected IRR */}
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_expected_irr')}
                </label>
                <div className='flex items-center space-x-4'>
                  <div className='relative flex-1'>
                    <TrendingUp className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                    <input
                      type='text'
                      value={flipInputs.expectedIRR}
                      onChange={(e) => {
                        const formatted = formatNumberInput(
                          e.target.value,
                          true
                        );
                        setFlipInputs({
                          ...flipInputs,
                          expectedIRR: formatted,
                        });
                      }}
                      className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                      placeholder='12.0'
                    />
                  </div>
                  <div className='text-2xl font-bold text-purple-600'>
                    {flipInputs.expectedIRR}%
                  </div>
                </div>
              </div>

              {/* Project Duration */}
              {/* <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_project_duration')}
                </label>
                <div className='relative'>
                  <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='text'
                    value={flipInputs.projectDuration}
                    onChange={(e) => {
                      const formatted = formatNumberInput(
                        e.target.value,
                        false
                      );
                      // Limit to reasonable range
                      const num = parseNumberInput(formatted);
                      if (num <= 10) {
                        setFlipInputs({
                          ...flipInputs,
                          projectDuration: formatted,
                        });
                      }
                    }}
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                    placeholder='2'
                  />
                </div>
              </div> */}

              {/* Exit Multiple */}
              {/* <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  {t('expected_exit_multiple')}
                </label>
                <div className='relative'>
                  <BarChart3 className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                  <input
                    type='text'
                    value={flipInputs.exitMultiple}
                    onChange={(e) => {
                      const formatted = formatNumberInput(e.target.value, true);
                      // Limit to reasonable range
                      const num = parseNumberInput(formatted);
                      if (num <= 5) {
                        setFlipInputs({
                          ...flipInputs,
                          exitMultiple: formatted,
                        });
                      }
                    }}
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200'
                    placeholder='1.6'
                  />
                </div>
              </div> */}

              <div className='bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800'>
                <div className='text-center mb-4'>
                  <div className='text-sm text-gray-600 dark:text-gray-400'>
                    {t('expected_total_roi')}
                  </div>
                  <div className='text-3xl font-bold text-purple-600'>
                    {formatCurrency(flipResults.totalROI)}
                  </div>
                  {currency === 'USD' && (
                    <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                      ≈{' '}
                      {new Intl.NumberFormat('en-AE', {
                        style: 'currency',
                        currency: 'AED',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(flipResults.totalROI)}{' '}
                      {t('expected_aed')}
                    </div>
                  )}
                </div>
                <div className='grid grid-cols-1 gap-4 text-center'>
                  <div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {t('expected_flip_entry_fee')}
                    </div>
                    <div className='font-semibold text-gray-700 dark:text-gray-300'>
                      {formatCurrency(
                        parseNumberInput(flipInputs.investmentAmount) * 0.025
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reset Button */}
              <button
                onClick={() =>
                  setFlipInputs({
                    investmentAmount: '50000',
                    expectedIRR: '12.0',
                    projectDuration: '2',
                    exitMultiple: '1.6',
                  })
                }
                className='w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200'
              >
                {t('expected_reset')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
